import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { strings } from "@/i18n/strings";

export type Lang = "ar" | "en";
type Theme = "light" | "dark";

interface Prefs {
  lang: Lang;
  theme: Theme;
  dir: "rtl" | "ltr";
  t: (key: string) => string;
  toggleLang: () => void;
  toggleTheme: () => void;
}

const PrefsContext = createContext<Prefs | null>(null);

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => (localStorage.getItem("wn_lang") as Lang) || "ar");
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem("wn_theme") as Theme) || "light");
  const dir = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    localStorage.setItem("wn_lang", lang);
  }, [lang, dir]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("wn_theme", theme);
  }, [theme]);

  const t = (key: string) => strings[lang][key] ?? strings.ar[key] ?? key;

  return (
    <PrefsContext.Provider
      value={{
        lang, theme, dir, t,
        toggleLang: () => setLang((l) => (l === "ar" ? "en" : "ar")),
        toggleTheme: () => setTheme((x) => (x === "light" ? "dark" : "light")),
      }}
    >
      {children}
    </PrefsContext.Provider>
  );
}

export const usePrefs = () => useContext(PrefsContext) as Prefs;
